// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Implement quote extras card for up-selling tool kits and installation
 * kits.
 * @author James Reid
 */

// @ts-check

// @@imports-package
import { App } from "../../../app/App.js"
import { ListSelect } from "../../elements/ListSelect.js"

// @@imports-types
import { cloneTemplate } from "../../../utils/index.js"
import { StyledComponent } from "../../base/StyledComponent.js"
import { Drain, Extra, Product } from "../../../../../package/index.js"

// @@body
class QuoteExtrasCard extends HTMLElement {
    constructor() {
        super()
    }

    connectedCallback() {
        const card = cloneTemplate("quote-extras")

        this.append(card)
    }

    static get customName() {
        return "quote-extras-card"
    }
}

class ProductExtraCard extends StyledComponent {
    constructor() {
        super({ templateId: "product-extra-card" })
    }

    connectedCallback() {
        let [minimumPrice, header] = [Infinity, ""]
        for (const code of this.dataset.productCodes?.split(" ") || []) {
            // 
            const product = this.#quote.stock?.getProduct(code)
            if (!product || product.stock <= 0) {
                // this.template.querySelector("table")?.classList.add("closed")
                // this.template.querySelector("p.price")?.classList.add("closed");
                // /** @type {HTMLParagraphElement} */ (this
                //     .querySelector("p.small")).innerText = "(Unavailable)"
                // this.template.classList.add("inactive")
                continue
            }

            // 
            const [productHeader, productDetail] = product.tableDetail
            if (!header) { header = productHeader }
            else if (productHeader !== header) { continue }
            if (product.price < minimumPrice) { minimumPrice = product.price }

            // 
            const row = cloneTemplate("product-extra-row");
            /** @type {HTMLTableCellElement} */ (row
                .querySelector("td.detail"))
                .innerText = productDetail;
            /** @type {HTMLTableCellElement} */ (row
                .querySelector("td.price"))
                .innerText = `£${product.price.toFixed(2)}`

            // 
            const updateRowQuantity = () => {
                const quantity = this.#quote.calculator?.bom
                    .getLineItemQuantity(product) || 0;
                /** @type {HTMLTableCellElement} */ (row
                    .querySelector("td.quantity"))
                    .innerText = `${quantity}`

                if (quantity <= 0) {
                    row.querySelector("td span.button.primary")?.classList
                        .remove("inactive")
                    row.querySelector("td span.button.secondary")?.classList
                        .add("inactive")
                }
                else if (quantity >= product.stock) {
                    row.querySelector("td span.button.primary")?.classList
                        .add("inactive")
                    row.querySelector("td span.button.secondary")?.classList
                        .remove("inactive")
                }
                else {
                    row.querySelector("td span.button.primary")?.classList
                        .remove("inactive")
                    row.querySelector("td span.button.secondary")?.classList
                        .remove("inactive")
                }
            }
            updateRowQuantity()

            // 
            row.querySelector("td span.button.primary")
                ?.addEventListener("click", () => {
                    const extras = this.#quote.calculator?.extras
                    const quantity = Math.min(
                        (extras?.get(product.code) || 0) + 1, product.stock
                    )
                    extras?.set(product.code, quantity)
                    App.dispatchStateUpdated()
                    updateRowQuantity()
                })
            row.querySelector("td span.button.secondary")
                ?.addEventListener("click", () => {
                    const extras = this.#quote.calculator?.extras
                    const quantity = Math.max(
                        (extras?.get(product.code) || 0) - 1, 0
                    )
                    extras?.set(product.code, quantity)
                    App.dispatchStateUpdated()
                    updateRowQuantity()
                })

            this.template?.querySelector("tbody")?.append(row)
        }

        if (!this.template.querySelector("tbody")?.children.length) {
            this.template.querySelector("table")?.classList.add("closed")
            this.template.querySelector("p.price")?.classList.add("closed");
            this.template.querySelector("[data-accessor='extra-unavailable']")
                ?.classList.remove("closed")
            this.template.classList.add("inactive")
        }

        if (this.dataset.showFromPrice === "false") {
            this.template.querySelector("span.price")?.parentElement?.classList.add("closed")
        }

        // 
        /** @type {HTMLTableCellElement} */ (this.template
            .querySelector("span.price"))
            .innerText = `£${minimumPrice.toFixed(2)}`;
        /** @type {HTMLTableCellElement} */ (this.template
            .querySelector("th.--wide"))
            .innerText = header
    }

    get #quote() {
        return App.instance.state.quote
    }

    static get customName() {
        return "product-extra-card"
    }
}

// @@exports
export { QuoteExtrasCard, ProductExtraCard }
